import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import { useAppContext } from 'app/useAppContext'

import { useToast } from 'ui/components/Toast'
import { useId } from 'ui/helpers/useId'

import {
    FileUploadAction,
    FileUploadContext,
    FileUploadContextValue,
} from './hooks/useFileUploadContext'
import { FilestackFileUploadStrategy } from './strategies'
import { FileUploadStrategy, UploadedFile } from './types'
import { formatFileCount, getFilestackPath, validateFile } from './utils'

export type FileUploadContextProviderProps = Pick<
    FileUploadContextValue,
    'inputRef' | 'disabled' | 'accept' | 'maxFiles' | 'maxSize' | 'multiple' | 'name' | 'required'
> & {
    value?: File[]
    onValueChange?: (files: File[]) => void
    onUploadSuccess?: (files: UploadedFile[]) => void
    strategy?: FileUploadStrategy
}

export const FileUploadContextProvider: React.FC<FileUploadContextProviderProps> = ({
    children,
    inputRef,
    value: valueProp,
    onValueChange,
    disabled,
    accept,
    maxFiles,
    maxSize,
    multiple,
    name,
    required,
    strategy: strategyProp,
    onUploadSuccess,
}) => {
    const { selectedStack, workspaceAccount } = useAppContext()
    const filestackPath = getFilestackPath(selectedStack, workspaceAccount)
    const strategy: FileUploadStrategy = useMemo(() => {
        if (strategyProp) {
            return strategyProp
        }

        return new FilestackFileUploadStrategy(filestackPath)
    }, [strategyProp, filestackPath])

    const inputId = useId()
    const dropzoneId = useId()
    const listId = useId()
    const labelId = useId()

    const [{ files, isDraggingOver, urlCache }, dispatch] = useReducer(
        createFileUploadReducer(onValueChange),
        {
            files: [],
            urlCache: new WeakMap(),
            isDraggingOver: false,
        }
    )
    const filesRef = useRef(files)
    filesRef.current = files

    const isControlled = valueProp !== undefined
    // If the value is controlled, we need to set the files in the state.
    useEffect(() => {
        if (isControlled) {
            dispatch({ type: 'SET_FILES', files: valueProp })
        }
    }, [valueProp, isControlled, dispatch])

    // Remove references to URL objects in the cache when the component unmounts.
    useEffect(() => {
        return () => {
            for (const file of filesRef.current) {
                const cachedUrl = urlCache.get(file.file)
                if (cachedUrl) {
                    URL.revokeObjectURL(cachedUrl)
                }
            }
        }
    }, [urlCache])

    const onFileProgress = useRef((file: File) => {
        let frame = 0
        return (progress: number) => {
            if (frame) return
            frame = requestAnimationFrame(() => {
                frame = 0
                dispatch({
                    type: 'SET_PROGRESS',
                    file,
                    progress: Math.min(Math.max(0, progress), 100),
                })
            })
        }
    }).current

    const toast = useToast()

    const onUpload = useCallback(
        async (files: File[]): Promise<void> => {
            try {
                // Process each file individually
                const uploadPromises = files.map(async (file) => {
                    try {
                        const onProgress = onFileProgress(file)
                        const uploadedFile = await strategy.upload(file, onProgress)

                        dispatch({ type: 'SET_SUCCESS', file })

                        return uploadedFile
                    } catch (error) {
                        const errorMessage =
                            error instanceof Error ? error.message : 'Upload failed.'

                        requestAnimationFrame(() => {
                            dispatch({
                                type: 'SET_ERROR',
                                file,
                                error: errorMessage,
                            })
                        })

                        toast({
                            type: 'error',
                            title: 'File upload failed',
                            startIcon: { name: 'AlertCircle' },
                            helperText: errorMessage,
                        })
                    }
                })

                // Wait for all uploads to complete
                const results = await Promise.all(uploadPromises)
                const uploadedFiles = results.filter(Boolean) as UploadedFile[]

                onUploadSuccess?.(uploadedFiles)
            } catch (error) {
                // This handles any error that might occur outside the individual upload processes
                console.error(error)

                toast({
                    type: 'error',
                    title: 'There was an error uploading the files',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            }
        },
        [onFileProgress, strategy, toast, onUploadSuccess]
    )

    const onFilesUpload = useCallback(
        async (files: File[]) => {
            try {
                for (const file of files) {
                    // Reset progress to 0.
                    dispatch({ type: 'SET_PROGRESS', file, progress: 0 })
                }

                await onUpload(files)
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Upload failed'
                requestAnimationFrame(() => {
                    for (const file of files) {
                        dispatch({
                            type: 'SET_ERROR',
                            file,
                            error: errorMessage,
                        })
                    }
                })
            }
        },
        [onUpload]
    )

    const onFilesChange = useCallback(
        (originalFiles: File[]) => {
            if (disabled) return

            const files = filesRef.current

            let filesToProcess = [...originalFiles]

            if (maxFiles) {
                const currentCount = files.length
                const remainingSlotCount = Math.max(0, maxFiles - currentCount)

                if (remainingSlotCount < filesToProcess.length) {
                    const rejectedFiles = filesToProcess.slice(remainingSlotCount)

                    filesToProcess = filesToProcess.slice(0, remainingSlotCount)

                    if (rejectedFiles.length > 0) {
                        const formattedMaxFiles = formatFileCount(maxFiles)
                        const formattedTitleFiles = formatFileCount(rejectedFiles.length)

                        toast({
                            type: 'error',
                            title: `${formattedTitleFiles} not uploaded`,
                            startIcon: { name: 'AlertCircle' },
                            helperText: `Maximum ${formattedMaxFiles} allowed.`,
                        })
                    }
                }
            }

            const acceptedFiles: File[] = []
            const rejectedFiles: { file: File; message: string }[] = []

            for (const file of filesToProcess) {
                const [isValid, validationMessage] = validateFile(file, accept, maxSize)

                if (isValid) {
                    acceptedFiles.push(file)
                } else {
                    rejectedFiles.push({ file, message: validationMessage })

                    toast({
                        type: 'error',
                        title: 'File not uploaded',
                        startIcon: { name: 'AlertCircle' },
                        helperText: validationMessage,
                    })
                }
            }

            if (acceptedFiles.length > 0) {
                dispatch({ type: 'ADD_FILES', files: acceptedFiles })

                if (isControlled && onValueChange) {
                    const currentFiles = files.map((f) => f.file)
                    onValueChange([...currentFiles])
                }

                requestAnimationFrame(() => {
                    onFilesUpload(acceptedFiles)
                })
            }
        },
        [disabled, maxFiles, toast, accept, maxSize, isControlled, onValueChange, onFilesUpload]
    )

    const value = useMemo(
        () => ({
            disabled,
            files,
            urlCache,
            isDraggingOver,
            inputRef,
            dropzoneId,
            inputId,
            listId,
            labelId,
            dispatch,
            accept,
            maxFiles,
            maxSize,
            multiple,
            name,
            required,
            onFilesChange,
        }),
        [
            disabled,
            files,
            urlCache,
            isDraggingOver,
            inputRef,
            dropzoneId,
            inputId,
            listId,
            labelId,
            dispatch,
            accept,
            maxFiles,
            maxSize,
            multiple,
            name,
            required,
            onFilesChange,
        ]
    )

    return <FileUploadContext.Provider value={value}>{children}</FileUploadContext.Provider>
}

type FileUploadState = Pick<FileUploadContextValue, 'files' | 'urlCache' | 'isDraggingOver'>

function createFileUploadReducer(onValueChange?: (files: File[]) => void) {
    return (state: FileUploadState, action: FileUploadAction): FileUploadState => {
        const filesMap = new Map(state.files.map((file) => [file.file, file]))

        switch (action.type) {
            case 'ADD_FILES': {
                for (const file of action.files) {
                    filesMap.set(file, {
                        file,
                        progress: 0,
                        status: 'idle',
                    })
                }

                const files = Array.from(filesMap.values())

                const fileList = files.map((fileState) => fileState.file)
                onValueChange?.(fileList)

                return { ...state, files }
            }

            case 'SET_FILES': {
                const newFileSet = new Set(action.files)
                for (const existingFile of filesMap.keys()) {
                    if (!newFileSet.has(existingFile)) {
                        filesMap.delete(existingFile)
                    }
                }

                for (const file of action.files) {
                    const existingState = filesMap.get(file)
                    if (!existingState) {
                        filesMap.set(file, {
                            file,
                            progress: 0,
                            status: 'idle',
                        })
                    }
                }

                const files = Array.from(filesMap.values())

                return { ...state, files }
            }

            case 'SET_PROGRESS': {
                const fileState = filesMap.get(action.file)
                if (fileState) {
                    filesMap.set(action.file, {
                        ...fileState,
                        progress: action.progress,
                        status: 'uploading',
                    })
                }

                const files = Array.from(filesMap.values())

                return { ...state, files }
            }

            case 'SET_SUCCESS': {
                const fileState = filesMap.get(action.file)
                if (fileState) {
                    filesMap.set(action.file, {
                        ...fileState,
                        progress: 100,
                        status: 'success',
                    })
                }

                const files = Array.from(filesMap.values())

                return { ...state, files }
            }

            case 'SET_ERROR': {
                const fileState = filesMap.get(action.file)
                if (fileState) {
                    filesMap.set(action.file, {
                        ...fileState,
                        error: action.error,
                        status: 'error',
                    })
                }

                const files = Array.from(filesMap.values())

                return { ...state, files }
            }

            case 'REMOVE_FILE': {
                const cachedUrl = state.urlCache.get(action.file)
                if (cachedUrl) {
                    URL.revokeObjectURL(cachedUrl)
                    state.urlCache.delete(action.file)
                }

                filesMap.delete(action.file)

                const files = Array.from(filesMap.values())

                const fileList = files.map((fileState) => fileState.file)
                onValueChange?.(fileList)

                return { ...state, files }
            }

            case 'SET_IS_DRAGGING_OVER': {
                return { ...state, isDraggingOver: action.isDraggingOver }
            }

            case 'CLEAR': {
                for (const file of filesMap.keys()) {
                    const cachedUrl = state.urlCache.get(file)
                    if (cachedUrl) {
                        URL.revokeObjectURL(cachedUrl)
                        state.urlCache.delete(file)
                    }
                }

                filesMap.clear()

                onValueChange?.([])

                return { ...state, files: [] }
            }

            default:
                return state
        }
    }
}
